import { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, CircularProgress, ClickAwayListener, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useGetList, useTranslate } from 'react-admin';
import { parseCurrentErrorCodes } from '@/helpers/device/formatCurrentErrorCodes.js';
import { errorLevelChoices } from '@/components/global/options/selectOptions.jsx';
import { Palette } from '@/themes/themeOverrides/Palette';
import { DrawerLink } from '@/components/global/drawers/DrawerLink';

const StyledTooltipContent = styled(Box)(() => ({
    minWidth: 240,
    maxWidth: 360,
    maxHeight: 320,
    overflow: 'auto',
    fontSize: '11px',
    lineHeight: '16px',
    '& .tooltip-title': {
        fontWeight: 700,
        marginBottom: 8,
        paddingBottom: 8,
    },
    '& .error-item': {
        padding: '8px 0',
        borderBottom: `1px solid ${Palette.gray.gray200}`,
        '&:last-of-type': {
            borderBottom: 'none',
            paddingBottom: 0,
        },
        '&:first-of-type': {
            paddingTop: 0,
        },
    },
    '& .error-item-header': {
        fontWeight: 600,
        marginBottom: 4,
    },
    '& .error-item-row': {
        display: 'flex',
        gap: 8,
        marginTop: 2,
    },
    '& .error-item-label': {
        flexShrink: 0,
        width: 90,
        opacity: 0.8,
    },
    '& .error-item-value': {
        flex: 1,
        fontWeight: 400,
        wordBreak: 'break-word',
    },
    '& .tooltip-status': {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '4px 0',
    },
}));

const getErrorLevelName = level => {
    const errorLevel = errorLevelChoices.find(choice => choice.id === level);
    if (!errorLevel) {
        return level ?? '—';
    }
    return errorLevel.name.charAt(0).toUpperCase() + errorLevel.name.slice(1);
};

const DeviceCodeErrorTooltipContent = ({ codes, errors, isLoading, hasError, onLinkClick }) => {
    const translate = useTranslate();

    if (!codes.length) {
        return <Typography sx={{ fontSize: '11px', lineHeight: '16px' }}>codeError</Typography>;
    }

    if (isLoading) {
        return (
            <StyledTooltipContent>
                <Box className='tooltip-title'>codeError: {codes.join(', ')}</Box>
                <Box className='tooltip-status'>
                    <CircularProgress size={14} color='inherit' />
                    <span>{translate('common.loading')}</span>
                </Box>
            </StyledTooltipContent>
        );
    }

    if (hasError) {
        return (
            <StyledTooltipContent>
                <Box className='tooltip-title'>codeError: {codes.join(', ')}</Box>
                <Box className='tooltip-status'>{translate('common.errors.loadingData')}</Box>
            </StyledTooltipContent>
        );
    }

    const errorsByCode = new Map((errors ?? []).map(error => [Number(error.code), error]));

    return (
        <StyledTooltipContent onMouseDown={event => event.stopPropagation()} onClick={event => event.stopPropagation()}>
            <Box className='tooltip-title'>codeError: {codes.join(', ')}</Box>
            {codes.map(code => {
                const error = errorsByCode.get(Number(code));
                const errorLabel = error?.title || error?.name;
                const headerLabel = errorLabel ? code + ': ' + errorLabel : String(code);

                return (
                    <Box key={code} className='error-item'>
                        <Box className='error-item-header'>
                            {error?.id ? (
                                <DrawerLink
                                    resource='device-error'
                                    action='show'
                                    source={String(error.id)}
                                    label={headerLabel}
                                    sx={{ fontWeight: 600, paddingBottom: '5px', display: 'block' }}
                                    onClick={event => {
                                        event.stopPropagation();
                                        onLinkClick?.();
                                    }}
                                />
                            ) : (
                                headerLabel
                            )}
                        </Box>
                        {error ? (
                            <>
                                {error.name && error.title ? (
                                    <Box className='error-item-row'>
                                        <Box className='error-item-label'>{translate('resources.deviceError.labels.codeName')}</Box>
                                        <Box className='error-item-value'>{error.name}</Box>
                                    </Box>
                                ) : null}
                                <Box className='error-item-row'>
                                    <Box className='error-item-label'>{translate('resources.deviceError.labels.level')}</Box>
                                    <Box className='error-item-value'>{getErrorLevelName(error.level)}</Box>
                                </Box>
                                {error.solutionTip ? (
                                    <Box className='error-item-row'>
                                        <Box className='error-item-label'>{translate('resources.deviceError.labels.suggestions')}</Box>
                                        <Box className='error-item-value'>{error.solutionTip}</Box>
                                    </Box>
                                ) : null}
                                {error.type ? (
                                    <Box className='error-item-row'>
                                        <Box className='error-item-label'>{translate('common.type')}</Box>
                                        <Box className='error-item-value'>{error.type}</Box>
                                    </Box>
                                ) : null}
                            </>
                        ) : null}
                    </Box>
                );
            })}
        </StyledTooltipContent>
    );
};

DeviceCodeErrorTooltipContent.propTypes = {
    codes: PropTypes.arrayOf(PropTypes.number).isRequired,
    errors: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
            code: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
            name: PropTypes.string,
            title: PropTypes.string,
            level: PropTypes.number,
            solutionTip: PropTypes.string,
            type: PropTypes.string,
        }),
    ),
    isLoading: PropTypes.bool,
    hasError: PropTypes.bool,
    onLinkClick: PropTypes.func,
};

const DeviceCodeErrorTooltip = ({ currentErrorCodes, children }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [open, setOpen] = useState(false);
    const codes = parseCurrentErrorCodes(currentErrorCodes);
    const enabled = open && codes.length > 0;

    const { data, isFetching, error } = useGetList(
        'device-error',
        {
            pagination: { page: 1, perPage: Math.max(codes.length, 1) },
            sort: { field: 'code', order: 'ASC' },
            filter: { code_in: codes },
        },
        { enabled },
    );

    const errors = data ?? [];
    const isLoading = enabled && isFetching && data === undefined;

    const handleClose = () => setOpen(false);

    const handleTriggerClick = event => {
        event.stopPropagation();
        if (isMobile) {
            setOpen(prev => !prev);
        }
    };

    return (
        <ClickAwayListener onClickAway={handleClose}>
            <Box component='span' sx={{ display: 'inline-flex', width: 'max-content' }}>
                <Tooltip
                    open={open}
                    onOpen={isMobile ? undefined : () => setOpen(true)}
                    onClose={isMobile ? undefined : handleClose}
                    disableHoverListener={isMobile}
                    disableFocusListener={isMobile}
                    disableTouchListener={isMobile}
                    disableInteractive={false}
                    title={
                        <DeviceCodeErrorTooltipContent
                            codes={codes}
                            errors={errors}
                            isLoading={isLoading}
                            hasError={Boolean(error)}
                            onLinkClick={handleClose}
                        />
                    }
                    slotProps={{
                        popper: {
                            disablePortal: isMobile,
                        },
                        tooltip: {
                            sx: {
                                bgcolor: Palette.white,
                                color: Palette.black,
                                boxShadow: Palette.boxShadow.elevation16,
                                borderRadius: '12px',
                                padding: '16px',
                                maxWidth: 'none',
                            },
                        },
                    }}
                >
                    <Box
                        component='span'
                        sx={{ display: 'inline-flex', width: 'max-content' }}
                        onClick={handleTriggerClick}
                        onMouseDown={event => event.stopPropagation()}
                    >
                        {children}
                    </Box>
                </Tooltip>
            </Box>
        </ClickAwayListener>
    );
};

DeviceCodeErrorTooltip.propTypes = {
    currentErrorCodes: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    children: PropTypes.node.isRequired,
};

export default DeviceCodeErrorTooltip;
